import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Search, Loader2, X, Building2, Network, HardDrive, Crosshair, Command,
} from "lucide-react";
import { getJSON } from "../lib/api";
import { useAuthStore } from "../state/authStore";

const TYPE_META = {
    client: { icon: Building2, label: "Clients", tone: "text-accent-cyan" },
    subnet: { icon: Network, label: "Subnets", tone: "text-accent-mint" },
    hardware: { icon: HardDrive, label: "Hardware", tone: "text-accent-amber" },
    important_ip: { icon: Crosshair, label: "Important IPs", tone: "text-accent-red" },
};
const GROUP_ORDER = ["client", "subnet", "hardware", "important_ip"];

/** Spotlight-style global search. Cmd/Ctrl+K to open, ESC to close, debounce
 *  200ms, deep-link result navigation. Free tier sees the trigger but the
 *  panel shows a paywall card and routes to /pricing on click. */
export const GlobalSearch = () => {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const isPaid = user && ["pro", "msp", "admin"].includes((user.subscription_plan || "").toLowerCase());
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [highlight, setHighlight] = useState(0);
    const inputRef = useRef(null);
    const lastQRef = useRef("");

    const close = useCallback(() => {
        setOpen(false);
        setQ("");
        setResults([]);
        setHighlight(0);
    }, []);

    // Keyboard shortcut: Cmd/Ctrl+K opens, ESC closes.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setOpen((o) => !o);
            }
            if (e.key === "Escape" && open) {
                e.preventDefault();
                close();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, close]);

    // Focus input on open.
    useEffect(() => {
        if (open && inputRef.current) {
            // Tiny delay so the modal can mount before we focus.
            setTimeout(() => inputRef.current?.focus(), 60);
        }
    }, [open]);

    // Debounced search.
    useEffect(() => {
        if (!open || !isPaid) return;
        const term = q.trim();
        if (term.length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const handle = setTimeout(async () => {
            try {
                lastQRef.current = term;
                const data = await getJSON(`/ipam/search?q=${encodeURIComponent(term)}&limit=20`);
                // Drop stale responses: only commit if the term hasn't moved on.
                if (lastQRef.current === term) {
                    setResults(data.results || []);
                    setHighlight(0);
                }
            } catch (e) {
                if (!(e && e.status === 401)) {
                    // 401 means the session expired — let App-level guard handle it.
                    setResults([]);
                }
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(handle);
    }, [q, open, isPaid]);

    const grouped = useMemo(() => {
        const out = {};
        for (const r of results) {
            (out[r.type] ||= []).push(r);
        }
        return out;
    }, [results]);

    const flatOrder = useMemo(() => {
        const list = [];
        for (const t of GROUP_ORDER) {
            for (const r of (grouped[t] || [])) list.push(r);
        }
        return list;
    }, [grouped]);

    const go = useCallback((r) => {
        close();
        navigate(r.url);
    }, [navigate, close]);

    const onInputKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, flatOrder.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const target = flatOrder[highlight];
            if (target) go(target);
        }
    };

    if (!user) return null;

    return (
        <>
            <button
                type="button"
                onClick={() => isPaid ? setOpen(true) : navigate("/pricing")}
                className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-white/[0.06] bg-surface/40 text-text-muted hover:text-text-primary hover:border-accent-cyan/30 transition-colors text-[12px]"
                aria-label="Open global search"
                title={isPaid ? "Search (⌘K)" : "Upgrade to Pro to search"}
                data-testid="global-search-trigger"
            >
                <Search className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">Search…</span>
                <span className="hidden lg:inline-flex items-center gap-0.5 text-[10.5px] text-text-dim font-mono ml-1 px-1.5 py-0.5 rounded border border-white/[0.06]">
                    <Command className="w-2.5 h-2.5" />K
                </span>
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-[80] flex items-start justify-center pt-[10vh] px-4 bg-canvas/80 backdrop-blur-md"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Global search"
                    data-testid="global-search-modal"
                >
                    <button type="button" aria-label="Close" onClick={close} className="absolute inset-0 cursor-default" />
                    <div className="relative w-full max-w-xl rounded-2xl border border-white/[0.08] bg-surface/95 shadow-heavy-popover overflow-hidden">
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
                            <Search className="w-4 h-4 text-text-dim" />
                            <input
                                ref={inputRef}
                                type="search"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={onInputKey}
                                placeholder="Search clients, subnets, hardware, important IPs…"
                                className="flex-1 bg-transparent text-text-primary text-[14px] placeholder:text-text-dim focus:outline-none"
                                data-testid="global-search-input"
                            />
                            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-dim" />}
                            <button
                                type="button"
                                onClick={close}
                                aria-label="Close"
                                className="p-1 text-text-dim hover:text-text-primary"
                                data-testid="global-search-close"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto">
                            {q.trim().length < 2 ? (
                                <div className="px-4 py-10 text-center text-text-dim text-[12.5px]" data-testid="global-search-hint">
                                    Type at least 2 characters. Use ↑↓ to navigate, Enter to open.
                                </div>
                            ) : !loading && flatOrder.length === 0 ? (
                                <div className="px-4 py-10 text-center text-text-dim text-[12.5px]" data-testid="global-search-empty">
                                    No matches for <span className="text-text-muted">"{q}"</span>
                                </div>
                            ) : (
                                <div className="py-2">
                                    {GROUP_ORDER.map((t) => {
                                        const list = grouped[t] || [];
                                        if (list.length === 0) return null;
                                        const meta = TYPE_META[t];
                                        const Icon = meta.icon;
                                        return (
                                            <div key={t} className="px-2 py-1" data-testid={`global-search-group-${t}`}>
                                                <p className={`px-2 py-1 text-[10.5px] font-mono uppercase tracking-[0.18em] ${meta.tone}`}>{meta.label}</p>
                                                {list.map((r) => {
                                                    const overallIdx = flatOrder.findIndex((x) => x === r);
                                                    const active = overallIdx === highlight;
                                                    return (
                                                        <button
                                                            key={r.id}
                                                            type="button"
                                                            onClick={() => go(r)}
                                                            onMouseEnter={() => setHighlight(overallIdx)}
                                                            className={`w-full text-left px-2 py-2 rounded-md flex items-center gap-3 ${
                                                                active ? "bg-elevated/80" : "hover:bg-elevated/50"
                                                            }`}
                                                            data-testid={`global-search-result-${r.type}-${r.id}`}
                                                        >
                                                            <Icon className={`w-3.5 h-3.5 ${meta.tone} flex-shrink-0`} />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-[13px] text-text-primary truncate">{r.label}</div>
                                                                <div className="text-[11px] text-text-dim truncate">{r.context}</div>
                                                            </div>
                                                            <span className="text-[10px] font-mono text-text-dim uppercase">{r.type.replace("_", " ")}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GlobalSearch;
